import { IconCheck, IconErrorOutline, IconResend } from "@/assets/icons";
import { Input } from "@/components/elements/Input";
import { Modal } from "@/components/elements/Modal";
import { background, button, divider, state, text } from "@/theme/colors";
import { rounded } from "@/theme/variables";
import { Box, Radio, Stack, styled, Typography } from "@mui/material";
import { ChangeEvent, FC, useState } from "react";

const BRAND_NAVY = "#0A1E3C";

const DEFAULT_LINK_BENEFITS = ["特定技能外国人を受験者として自動取り込み", "所属機関の自動取り込み"];
const DEFAULT_CONTRACT_LABEL = "irohana visa の登録支援機関様向けの契約";

type TAuthMethod = "self" | "delegate";

type TAuthMethodOption = {
  value: TAuthMethod;
  label: string;
  descriptions: string[];
};

const AUTH_METHOD_OPTIONS: TAuthMethodOption[] = [
  {
    value: "self",
    label: "自分で認証する",
    descriptions: ["あなた自身が irohana visa の有効なユーザーの場合。", "新しいタブで irohana visa を開き、その場で認証します。"],
  },
  {
    value: "delegate",
    label: "担当者に依頼する",
    descriptions: ["irohana visa の担当者が別の方の場合。", "担当者へ認証用リンクをメールで送信します。"],
  },
];

const SELF_SELECTED_NOTES = ["「irohana visa で認証する」を押すと別タブが開きます。", "認証完了後、自動的に連携が有効になります。"];

// 担当者メールの簡易形式チェック (このモーダルは react-hook-form を使わず手動バリデーションのため)。
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export type TVisaLinkSubmitPayload = {
  authType: TAuthMethod;
  licenseNumber: string;
  /** 担当者メール。authType が "delegate" の場合のみ設定 (BE のフィールド名に合わせて email) */
  email?: string;
};

type TVisaLinkModalProps = {
  isOpen: boolean;
  onClose: () => void;
  /** 送信ボタン押下時。Promise を返すと送信中はローディング表示し、reject 時はモーダルを開いたまま再入力可能にする。 */
  onOk?: (payload: TVisaLinkSubmitPayload) => void | Promise<void>;
  /** 番号の形式チェック用パターン（未指定なら形式チェックなし） */
  licenseNumberPattern?: RegExp;
  /** 番号が形式に合わない場合のエラーメッセージ */
  licenseNumberErrorMessage?: string;
  /** 番号入力欄のラベル。RSO=登録番号 (default) / Company=管理番号 */
  licenseNumberLabel?: string;
  /** 番号入力欄のプレースホルダー */
  licenseNumberPlaceholder?: string;
  /** 連携で得られるメリット一覧。RSO は所属機関の自動取り込みを含む (default)、Company は除外する。 */
  linkBenefits?: string[];
  /** 警告文の契約ラベル (太字部分)。RSO=登録支援機関様向けの契約 (default) / Company=irohana visa の契約 */
  contractLabel?: string;
};

export const VisaLinkModal: FC<TVisaLinkModalProps> = ({
  isOpen,
  onClose,
  onOk,
  licenseNumberPattern,
  licenseNumberErrorMessage,
  licenseNumberLabel = "登録番号",
  licenseNumberPlaceholder = "入力してください",
  linkBenefits = DEFAULT_LINK_BENEFITS,
  contractLabel = DEFAULT_CONTRACT_LABEL,
}) => {
  const [authMethod, setAuthMethod] = useState<TAuthMethod | null>(null);
  const [licenseNumber, setLicenseNumber] = useState("");
  const [delegateEmail, setDelegateEmail] = useState("");
  const [licenseError, setLicenseError] = useState<string | null>(null);
  const [delegateEmailError, setDelegateEmailError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleClose = () => {
    setAuthMethod(null);
    setLicenseNumber("");
    setDelegateEmail("");
    setLicenseError(null);
    setDelegateEmailError(null);
    onClose();
  };

  const handleLicenseChange = (e: ChangeEvent<HTMLInputElement>) => {
    setLicenseNumber(e.target.value);
    if (licenseError) setLicenseError(null);
  };

  const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
    setDelegateEmail(e.target.value);
    if (delegateEmailError) setDelegateEmailError(null);
  };

  // 認証方法を切り替えたときは番号エラーをリセットする。
  // エラー状態は親で共有しているため、切替時にクリアしないと未フォーカスの入力欄にエラーが引き継がれてしまう。
  // (バリデーションは入力欄を blur したときのみ表示する)
  const handleAuthMethodChange = (value: TAuthMethod) => {
    // 同じ option 内をクリックしただけ (option 変更なし) のときは入力を保持する。
    // OptionCard 全体が clickable なため、選択中の option 内を触るたびにリセットしてしまうのを防ぐ。
    if (value === authMethod) return;
    // option を切り替えたら入力内容とエラーをリセットする。
    setAuthMethod(value);
    setLicenseNumber("");
    setDelegateEmail("");
    setLicenseError(null);
    setDelegateEmailError(null);
  };

  const validateLicenseNumber = (value: string) => {
    // 空欄は必須エラーを表示する (入力欄を focus → blur したときに未入力を促す)。
    if (!value) return `${licenseNumberLabel}を入力してください`;
    if (licenseNumberPattern && !licenseNumberPattern.test(value)) {
      return licenseNumberErrorMessage ?? "形式が正しくありません";
    }
    return null;
  };

  const validateEmail = (value: string) => {
    // 番号入力と同じく、空欄は必須エラー・形式不正は形式エラーを blur 時に表示する。
    if (!value) return "担当者のメールアドレスを入力してください";
    if (!EMAIL_REGEX.test(value)) return "メールアドレスの形式が正しくありません";
    return null;
  };

  const handleOk = async () => {
    if (!authMethod) return;
    const trimmedLicense = licenseNumber.trim();
    const licenseErr = validateLicenseNumber(trimmedLicense);
    const trimmedEmail = delegateEmail.trim();
    const emailErr = authMethod === "delegate" ? validateEmail(trimmedEmail) : null;
    if (licenseErr || emailErr) {
      setLicenseError(licenseErr);
      setDelegateEmailError(emailErr);
      return;
    }
    try {
      setIsSubmitting(true);
      await onOk?.({
        authType: authMethod,
        licenseNumber: trimmedLicense,
        email: authMethod === "delegate" ? trimmedEmail : undefined,
      });
      handleClose();
    } catch {
      // 送信失敗時は呼び出し側でエラー表示する。モーダルは開いたまま再入力できるようにする。
    } finally {
      setIsSubmitting(false);
    }
  };

  const okLabel = !authMethod ? (
    "認証方法が未選択"
  ) : (
    <Stack direction="row" alignItems="center" gap={1}>
      {authMethod === "self" ? <IconCheck fontSize={20} /> : <IconResend />}
      {authMethod === "self" ? "irohana visa で認証" : "認証メールを送信"}
    </Stack>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      onOk={handleOk}
      title={<ModalTitle />}
      labelClose="キャンセル"
      labelOk={okLabel}
      isLoadingOkBtn={isSubmitting}
      disabledOk={isSubmitting || !authMethod || !licenseNumber.trim() || (authMethod === "delegate" && !delegateEmail.trim())}
      preventCloseBackdrop
      enableDivider
      width={560}
    >
      <Stack gap={2}>
        <Typography variant="body14" color={text.secondary}>
          irohana visa に登録された特定技能外国人を、
          <Box component="span" sx={{ fontWeight: 700, color: text.primary }}>
            irohana study の学習者として取り込み・同期
          </Box>
          します。連携後は最新の在留情報が自動で反映されます。
        </Typography>
        <Stack gap={1.5} sx={{ backgroundColor: background.primary, borderRadius: rounded.sm, p: 2 }}>
          {linkBenefits.map((benefit) => (
            <Stack key={benefit} direction="row" alignItems="center" gap={1.5}>
              <IconCheck color={state.success_1} fontSize={20} style={{ flexShrink: 0 }} />
              <Typography variant="body14" color={text.primary}>
                {benefit}
              </Typography>
            </Stack>
          ))}
        </Stack>
        <Stack
          direction="row"
          alignItems="flex-start"
          gap={1.5}
          sx={{
            backgroundColor: state.incomplete_secondary,
            border: `1px solid ${state.warning_1}`,
            borderRadius: rounded.sm,
            p: 2,
          }}
        >
          <IconErrorOutline color={state.warning_1} fontSize={22} style={{ flexShrink: 0 }} />
          <Typography variant="body14" color={text.secondary}>
            この連携は
            <Box component="span" sx={{ fontWeight: 700, color: state.warning_1 }}>
              {contractLabel}
            </Box>
            と、
            <Box component="span" sx={{ fontWeight: 700, color: state.warning_1 }}>
              irohana visa にアクセス可能なユーザーによる認証・承認
            </Box>
            が必要です。
          </Typography>
        </Stack>
        <Stack gap={1.5}>
          <Typography variant="body14Bold" color={text.primary}>
            認証方法を選択してください
          </Typography>
          {AUTH_METHOD_OPTIONS.map((option) => {
            const isSelected = authMethod === option.value;
            return (
              <OptionCard key={option.value} selected={isSelected} onClick={() => handleAuthMethodChange(option.value)}>
                <Radio
                  checked={isSelected}
                  size="small"
                  disableRipple
                  sx={{ p: 0, mt: "2px", color: button.primary, "&.Mui-checked": { color: button.primary } }}
                />
                <Stack gap={0.5} flex={1}>
                  <Typography variant="body14Bold" color={text.primary}>
                    {option.label}
                  </Typography>
                  <Typography variant="cap12" color={text.secondary}>
                    {option.descriptions.map((line) => (
                      <Box key={line} component="span" display="block">
                        {line}
                      </Box>
                    ))}
                  </Typography>
                  {isSelected && (
                    <Stack gap={1} mt={1}>
                      <Typography variant="body14" color={text.primary}>
                        {licenseNumberLabel}
                      </Typography>
                      <Input
                        nonControl
                        // blur/focus 時の合成 onChange を無効化する。
                        // これを付けないと Input.handleBlur が onBlur の後に onChange を発火し、
                        // handleLicenseChange が licenseError をクリアして blur 時のエラーが即消えてしまう。
                        disableFieldOnChange
                        value={licenseNumber}
                        onChange={handleLicenseChange}
                        onBlur={() => setLicenseError(validateLicenseNumber(licenseNumber.trim()))}
                        error={Boolean(licenseError)}
                        helperText={licenseError ?? undefined}
                        placeholder={licenseNumberPlaceholder}
                        sx={{ ".MuiInputBase-root": { backgroundColor: background.white } }}
                      />
                      {option.value === "self" ? (
                        <Typography variant="cap12" color={text.secondary} mt={1}>
                          {SELF_SELECTED_NOTES.map((line) => (
                            <Box key={line} component="span" display="block">
                              {line}
                            </Box>
                          ))}
                        </Typography>
                      ) : (
                        <>
                          <Typography variant="body14" color={text.primary} mt={1}>
                            担当者のメールアドレス
                          </Typography>
                          <Input
                            nonControl
                            disableFieldOnChange
                            type="email"
                            value={delegateEmail}
                            onChange={handleEmailChange}
                            onBlur={() => setDelegateEmailError(validateEmail(delegateEmail.trim()))}
                            error={Boolean(delegateEmailError)}
                            helperText={delegateEmailError ?? undefined}
                            placeholder="入力してください"
                            sx={{ ".MuiInputBase-root": { backgroundColor: background.white } }}
                          />
                          <Typography variant="cap12" color={text.secondary}>
                            irohana visa の有効なユーザー宛に認証依頼を送信します。
                          </Typography>
                        </>
                      )}
                    </Stack>
                  )}
                </Stack>
              </OptionCard>
            );
          })}
        </Stack>
      </Stack>
    </Modal>
  );
};

const ModalTitle: FC = () => (
  <Stack direction="row" alignItems="center" gap={1}>
    <IrohanaFlowerMark size={26} />
    <Typography sx={{ fontSize: 22, fontWeight: 700, color: BRAND_NAVY, lineHeight: 1 }}>
      irohana&nbsp;
      <Box component="span" sx={{ fontSize: 17 }}>
        visa
      </Box>
    </Typography>
    <Typography variant="head20Bold" color={text.primary}>
      と連携
    </Typography>
  </Stack>
);

// irohana flower mark extracted from src/assets/images/logo.svg
const IrohanaFlowerMark: FC<{ size?: number }> = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 53 53" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M42.749 49.6168C38.6171 52.625 33.2584 52.7154 29.1172 50.2839C29.5998 49.7821 30.0482 49.2428 30.4623 48.6755L32.9533 45.2434C34.9056 46.0944 37.244 45.9136 39.0873 44.5732C41.8678 42.5532 42.4843 38.6474 40.4666 35.8668L40.1117 35.3774L38.1874 32.7247L34.3389 27.4223L32.4146 24.7695L30.9574 22.7651L37.1848 20.7358L41.0458 26.0569L42.9545 28.6879L45.5046 32.201C45.9063 32.7527 46.2519 33.3263 46.5508 33.9154C47.0335 34.8693 47.3822 35.8668 47.6002 36.8799C48.6152 41.562 46.8684 46.6181 42.749 49.6168Z"
      fill="#32BE32"
    />
    <path
      d="M38.0904 7.48131C37.4147 7.60605 36.7359 7.77435 36.0602 7.99258L32.0342 9.29867C30.9506 7.46575 28.9548 6.23443 26.6755 6.23443C23.2411 6.23443 20.4481 9.0306 20.4481 12.4689V28.6659L14.2207 26.6366V12.4689C14.2207 11.7862 14.2767 11.116 14.3826 10.4645C14.5539 9.40777 14.8559 8.3978 15.2763 7.45019C17.2005 3.06425 21.5815 0 26.6755 0C31.782 0 36.1723 3.07673 38.0904 7.48131Z"
      fill="#E6285A"
    />
    <path
      d="M36.2631 35.3773L32.4021 40.6984L30.4934 43.3293L27.9433 46.8424C27.5416 47.3942 27.1057 47.9023 26.6386 48.3668C25.882 49.1211 25.0444 49.7602 24.1477 50.2838C20.0127 52.6966 14.6695 52.5968 10.547 49.5981C6.41511 46.5931 4.67144 41.5214 5.70519 36.8268C6.32482 37.1292 6.97557 37.3879 7.64814 37.6093L11.671 38.9185C11.4687 41.0413 12.3623 43.214 14.2056 44.5544C16.9861 46.5775 20.8876 45.9571 22.9053 43.1766L23.2603 42.6903L25.1845 40.0376L29.03 34.7352H29.0331L30.9574 32.0855L32.4146 30.075L36.2631 35.3773Z"
      fill="#32BEE6"
    />
    <path
      d="M25.9167 33.722L22.666 38.2045L22.0713 39.0244L15.822 36.9919L12.7333 35.9851L8.61069 34.6447C7.96305 34.4327 7.34343 34.174 6.75805 33.8716C5.80836 33.3853 4.93964 32.7837 4.16744 32.0917C0.596008 28.9028 -0.960845 23.7843 0.611573 18.9339C2.19022 14.068 6.47157 10.8416 11.2511 10.374C11.1546 11.066 11.1079 11.7705 11.1079 12.4688V16.702C9.02793 17.1664 7.23755 18.6876 6.53385 20.8603C5.4752 24.1303 7.26868 27.6528 10.535 28.7157L11.1079 28.9028L23.5627 32.9551L25.9167 33.722Z"
      fill="#5A5FE6"
    />
    <path
      d="M49.1354 32.1446C48.8022 31.5212 48.4255 30.9227 48.0238 30.3678L45.5391 26.942C46.9464 25.3428 47.4976 23.0579 46.7939 20.8883C45.7321 17.6184 42.2136 15.8229 38.9473 16.8858L38.3744 17.0729L25.9196 21.1252L23.5625 21.8921V15.3366L29.8086 13.3042L32.8974 12.2973L37.0231 10.9569C37.6707 10.7481 38.3246 10.5922 38.9754 10.4925C40.0309 10.3272 41.0833 10.3023 42.114 10.4114C46.8779 10.8883 51.1406 14.1115 52.7161 18.9619C54.2948 23.8279 52.7255 28.9557 49.1354 32.1446Z"
      fill="#FAA032"
    />
  </svg>
);

const OptionCard = styled(Stack, { shouldForwardProp: (propName: string) => propName !== "selected" })<{ selected: boolean }>`
  flex-direction: row;
  gap: 12px;
  padding: 16px;
  border-radius: ${rounded.sm};
  border: 1px solid ${({ selected }) => (selected ? button.primary : divider.high)};
  background-color: ${({ selected }) => (selected ? button.hover_02 : background.white)};
  cursor: pointer;
  transition:
    border-color 0.2s,
    background-color 0.2s;
  :hover {
    border-color: ${button.primary};
  }
`;
